import { useEffect, useMemo, useState } from "react";
import { Plus, Edit3, Trash2, Loader2, Layers, Link2, Unlink } from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";
import { Toast, FieldInput, FieldTextarea, FieldSelect, FieldToggle, DialogShell, ConfirmDialog } from "../components/AdminUI";

const TOKEN_KEY = "nailEvent.adminToken";
const EMPTY_COL = { name: "", description: "", domain: "", language: "vi", active: true };
const EMPTY_AGENT = {
  name: "", description: "", system_prompt: "", tone: "professional",
  default_language: "vi", supported_languages: "vi,en",
  model_provider: "openai", model_name: "gpt-5.4", temperature: "0.3",
  voice_mapping: "", signalwire_number: "", openai_assistant_id: "",
  conversation_retention_days: 90, active: true,
};

export default function AdminAiBrain() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [tab, setTab] = useState("agents");
  const [agents, setAgents] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [editingAgent, setEditingAgent] = useState(null);
  const [editingCol, setEditingCol] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [attachOpen, setAttachOpen] = useState(null); // agent for attach panel

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/ai/agents", { headers }),
        api.get("/admin/ai/collections", { headers }),
      ]);
      setAgents(r1.data.agents || []);
      setCollections(r2.data.collections || []);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveAgent = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/ai/agents/${id}`, payload, { headers });
      else await api.post("/admin/ai/agents", payload, { headers });
      setEditingAgent(null); flash(L("Đã lưu", "Saved")); load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const saveCol = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/ai/collections/${id}`, payload, { headers });
      else await api.post("/admin/ai/collections", payload, { headers });
      setEditingCol(null); flash(L("Đã lưu", "Saved")); load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const removeAgent = async (id) => {
    try { await api.delete(`/admin/ai/agents/${id}`, { headers }); setConfirmDelete(null); flash(L("Đã xóa", "Deleted")); load(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const removeCol = async (id) => {
    try { await api.delete(`/admin/ai/collections/${id}`, { headers }); setConfirmDelete(null); flash(L("Đã xóa", "Deleted")); load(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const attach = async (agent_id, collection_id) => {
    try { await api.post(`/admin/ai/agents/${agent_id}/collections`, { collection_id }, { headers }); load(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const detach = async (agent_id, collection_id) => {
    try { await api.delete(`/admin/ai/agents/${agent_id}/collections/${collection_id}`, { headers }); load(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <AdminShell
      testid="admin-ai-brain"
      subtitle={L("Một Bộ Não AI", "One AI Brain")}
      title={L("Trợ Lý & Bộ Sưu Tập", "Agents & Collections")}
      actions={
        tab === "agents" ? (
          <button type="button" onClick={() => setEditingAgent({ mode: "create", data: { ...EMPTY_AGENT } })} data-testid="agent-add-btn"
            className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
            <Plus className="w-4 h-4" /> {L("Thêm Agent", "Add Agent")}
          </button>
        ) : (
          <button type="button" onClick={() => setEditingCol({ mode: "create", data: { ...EMPTY_COL } })} data-testid="collection-add-btn"
            className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
            <Plus className="w-4 h-4" /> {L("Thêm Bộ Sưu Tập", "Add Collection")}
          </button>
        )
      }
    >
      <div className="max-w-[1400px]">
        <Toast message={toast} testId="ai-toast" />

        <div className="inline-flex bg-white border border-espresso/10 rounded-full p-1 mb-6">
          <button type="button" onClick={() => setTab("agents")} data-testid="tab-agents"
            className={`px-5 py-2 text-sm font-medium rounded-full ${tab === "agents" ? "bg-espresso text-linen" : "text-espresso-soft hover:text-espresso"}`}>
            {L("Trợ Lý", "Agents")} ({agents.length})
          </button>
          <button type="button" onClick={() => setTab("collections")} data-testid="tab-collections"
            className={`px-5 py-2 text-sm font-medium rounded-full ${tab === "collections" ? "bg-espresso text-linen" : "text-espresso-soft hover:text-espresso"}`}>
            {L("Bộ Sưu Tập", "Collections")} ({collections.length})
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : tab === "agents" ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((a) => (
              <div key={a.id} data-testid={`agent-card-${a.id}`} className="bg-white border border-espresso/10 rounded-3xl p-5 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald2">{a.slug}</span>
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full ${a.active ? "bg-emerald2/10 text-emerald2" : "bg-warmgray-100 text-warmgray-700"}`}>{a.active ? L("Bật", "Active") : L("Tắt", "Off")}</span>
                </div>
                <div className="font-serif text-lg text-espresso">{a.name}</div>
                {a.description && <p className="text-sm text-espresso-soft line-clamp-2">{a.description}</p>}
                <div className="text-[10px] text-espresso-muted">{a.model_provider} · {a.model_name} · temp {a.temperature}</div>
                <div className="mt-1 text-[10px] text-espresso-muted">
                  <Layers className="w-3 h-3 inline mr-1" /> {a.collection_ids?.length || 0} {L("bộ sưu tập đính kèm", "collections attached")}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => setAttachOpen(a)} data-testid={`agent-attach-${a.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso" title={L("Đính kèm", "Attach")}>
                    <Link2 className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setEditingAgent({ mode: "edit", data: { ...a, supported_languages: (a.supported_languages || []).join(",") } })} data-testid={`agent-edit-${a.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setConfirmDelete({ type: "agent", item: a })} data-testid={`agent-delete-${a.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.length === 0 ? (
              <div className="md:col-span-full bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted">
                {L("Chưa có bộ sưu tập. Bộ sưu tập nhóm các tài liệu (IRS, OSHA, California State Board...) và được đính kèm vào nhiều Agent.", "No collections yet. Collections group documents (IRS, OSHA, California State Board...) and attach to multiple Agents.")}
              </div>
            ) : collections.map((c) => (
              <div key={c.id} data-testid={`col-card-${c.id}`} className="bg-white border border-espresso/10 rounded-3xl p-5 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald2">{c.slug}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-espresso-muted">{c.item_count} {L("tài liệu", "docs")}</span>
                </div>
                <div className="font-serif text-lg text-espresso">{c.name}</div>
                {c.description && <p className="text-sm text-espresso-soft line-clamp-2">{c.description}</p>}
                <div className="text-[10px] text-espresso-muted">{c.domain} · {c.language?.toUpperCase()}</div>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => setEditingCol({ mode: "edit", data: c })} data-testid={`col-edit-${c.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setConfirmDelete({ type: "collection", item: c })} data-testid={`col-delete-${c.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingAgent && (
        <AgentEditDialog mode={editingAgent.mode} initial={editingAgent.data} onClose={() => setEditingAgent(null)}
          onSave={(p) => saveAgent(p, editingAgent.mode === "edit" ? editingAgent.data.id : null)} L={L} />
      )}
      {editingCol && (
        <CollectionEditDialog mode={editingCol.mode} initial={editingCol.data} onClose={() => setEditingCol(null)}
          onSave={(p) => saveCol(p, editingCol.mode === "edit" ? editingCol.data.id : null)} L={L} />
      )}
      {attachOpen && (
        <AttachPanel agent={attachOpen} collections={collections} onClose={() => setAttachOpen(null)}
          onAttach={(cid) => attach(attachOpen.id, cid)} onDetach={(cid) => detach(attachOpen.id, cid)} L={L} />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title={confirmDelete.type === "agent" ? L("Xóa Agent?", "Delete Agent?") : L("Xóa Bộ Sưu Tập?", "Delete Collection?")}
          body={L("Hành động này không thể hoàn tác.", "This cannot be undone.")}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => confirmDelete.type === "agent" ? removeAgent(confirmDelete.item.id) : removeCol(confirmDelete.item.id)}
          L={L} />
      )}
    </AdminShell>
  );
}

function AgentEditDialog({ mode, initial, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.system_prompt?.trim()) return;
    setSaving(true);
    const supported = typeof form.supported_languages === "string" ? form.supported_languages.split(",").map((s) => s.trim()).filter(Boolean) : (form.supported_languages || []);
    await onSave({
      name: form.name.trim(),
      description: form.description || null,
      system_prompt: form.system_prompt,
      tone: form.tone || "professional",
      default_language: form.default_language || "vi",
      supported_languages: supported,
      model_provider: form.model_provider || "openai",
      model_name: form.model_name || "gpt-5.4",
      temperature: form.temperature || "0.3",
      voice_mapping: form.voice_mapping || null,
      signalwire_number: form.signalwire_number || null,
      openai_assistant_id: form.openai_assistant_id || null,
      conversation_retention_days: parseInt(form.conversation_retention_days || "90", 10),
      active: !!form.active,
    });
    setSaving(false);
  };
  return (
    <DialogShell title={mode === "edit" ? L("Chỉnh sửa Agent", "Edit Agent") : L("Thêm Agent", "Add Agent")}
      onClose={onClose} onSubmit={submit} saving={saving} testIdPrefix="agent" maxWidth="max-w-3xl">
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label={L("Tên *", "Name *")} value={form.name} onChange={(v) => set("name", v)} testId="agent-name" required />
        <FieldSelect label={L("Tông giọng", "Tone")} value={form.tone} onChange={(v) => set("tone", v)} testId="agent-tone"
          options={[["professional", "Professional"], ["friendly", "Friendly"], ["educational", "Educational"], ["concise", "Concise"]]} />
        <FieldTextarea label={L("Mô tả", "Description")} value={form.description} onChange={(v) => set("description", v)} rows={2} testId="agent-description" className="md:col-span-2" />
        <FieldTextarea label={L("Prompt hệ thống *", "System prompt *")} value={form.system_prompt} onChange={(v) => set("system_prompt", v)} rows={8} testId="agent-prompt" className="md:col-span-2" />
        <FieldSelect label={L("Ngôn ngữ mặc định", "Default language")} value={form.default_language} onChange={(v) => set("default_language", v)} testId="agent-lang"
          options={[["vi", "Tiếng Việt"], ["en", "English"], ["es", "Español"], ["zh", "中文"], ["ko", "한국어"]]} />
        <FieldInput label={L("Ngôn ngữ hỗ trợ (vi,en)", "Supported languages (vi,en)")} value={form.supported_languages} onChange={(v) => set("supported_languages", v)} testId="agent-langs" />
        <FieldInput label={L("Provider", "Provider")} value={form.model_provider} onChange={(v) => set("model_provider", v)} testId="agent-provider" placeholder="openai" />
        <FieldInput label={L("Model", "Model")} value={form.model_name} onChange={(v) => set("model_name", v)} testId="agent-model" placeholder="gpt-5.4" />
        <FieldInput label={L("Temperature", "Temperature")} value={form.temperature} onChange={(v) => set("temperature", v)} testId="agent-temp" placeholder="0.3" />
        <FieldInput label={L("Lưu hội thoại (ngày)", "Conv. retention (days)")} value={form.conversation_retention_days} onChange={(v) => set("conversation_retention_days", v)} type="number" testId="agent-retention" />
        <FieldInput label={L("Voice mapping (tương lai)", "Voice mapping (future)")} value={form.voice_mapping} onChange={(v) => set("voice_mapping", v)} testId="agent-voice" />
        <FieldInput label={L("Số SignalWire (tương lai)", "SignalWire number (future)")} value={form.signalwire_number} onChange={(v) => set("signalwire_number", v)} placeholder="+1833..." testId="agent-signalwire" />
        <FieldInput label={L("OpenAI Assistant ID (tương lai)", "OpenAI Assistant ID (future)")} value={form.openai_assistant_id} onChange={(v) => set("openai_assistant_id", v)} testId="agent-assistant" />
        <FieldToggle label={L("Kích hoạt", "Active")} checked={form.active} onChange={(v) => set("active", v)} testId="agent-active" />
      </div>
    </DialogShell>
  );
}

function CollectionEditDialog({ mode, initial, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return;
    setSaving(true);
    await onSave({
      name: form.name.trim(),
      description: form.description || null,
      domain: form.domain || null,
      language: form.language || "vi",
      active: !!form.active,
    });
    setSaving(false);
  };
  return (
    <DialogShell title={mode === "edit" ? L("Chỉnh sửa Bộ Sưu Tập", "Edit Collection") : L("Thêm Bộ Sưu Tập", "Add Collection")}
      onClose={onClose} onSubmit={submit} saving={saving} testIdPrefix="col" maxWidth="max-w-lg">
      <div className="grid gap-4">
        <FieldInput label={L("Tên *", "Name *")} value={form.name} onChange={(v) => set("name", v)} testId="col-name" required />
        <FieldTextarea label={L("Mô tả", "Description")} value={form.description} onChange={(v) => set("description", v)} rows={2} testId="col-description" />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput label={L("Lĩnh vực", "Domain")} value={form.domain} onChange={(v) => set("domain", v)} placeholder="tax / legal / hr / state_board / asterpos" testId="col-domain" />
          <FieldSelect label={L("Ngôn ngữ", "Language")} value={form.language} onChange={(v) => set("language", v)} testId="col-lang"
            options={[["vi", "Tiếng Việt"], ["en", "English"], ["es", "Español"], ["any", L("Bất kỳ", "Any")]]} />
        </div>
        <FieldToggle label={L("Kích hoạt", "Active")} checked={form.active} onChange={(v) => set("active", v)} testId="col-active" />
      </div>
    </DialogShell>
  );
}

function AttachPanel({ agent, collections, onClose, onAttach, onDetach, L }) {
  const attached = new Set(agent.collection_ids || []);
  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="attach-panel">
      <div className="w-full max-w-lg bg-white rounded-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-5 border-b border-warmgray-200">
          <h3 className="font-serif text-2xl text-charcoal">{L("Đính kèm Bộ Sưu Tập", "Attach Collections")}</h3>
          <p className="text-xs text-warmgray-700 mt-1">{L("Agent:", "Agent:")} <span className="font-medium">{agent.name}</span></p>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {collections.length === 0 ? (
            <p className="text-sm text-warmgray-700">{L("Tạo Bộ Sưu Tập trước.", "Create a Collection first.")}</p>
          ) : collections.map((c) => {
            const on = attached.has(c.id);
            return (
              <button key={c.id} type="button" onClick={() => on ? onDetach(c.id) : onAttach(c.id)} data-testid={`attach-toggle-${c.id}`}
                className={`w-full flex items-center justify-between gap-3 p-3 rounded-2xl border mb-2 ${on ? "border-emerald2 bg-emerald2/5" : "border-warmgray-200 hover:border-warmgray-300"}`}>
                <div className="text-left">
                  <div className="font-medium text-charcoal">{c.name}</div>
                  <div className="text-xs text-warmgray-700">{c.description || "—"}</div>
                </div>
                {on ? <Unlink className="w-4 h-4 text-emerald2" /> : <Link2 className="w-4 h-4 text-warmgray-500" />}
              </button>
            );
          })}
        </div>
        <div className="px-6 py-4 border-t border-warmgray-200 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="attach-close">{L("Đóng", "Close")}</button>
        </div>
      </div>
    </div>
  );
}
